import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../components/Layout';
import { leadAPI } from '../utils/api';
import { formatDate } from '../utils/helpers';
import { toast } from 'sonner';
import {
  Plus, X, Inbox, Phone, Instagram, Mail, Calendar as CalIcon, MapPin,
  Trash2, ArrowRight, Briefcase, TrendingUp, DollarSign, Sparkles, Bell,
} from 'lucide-react';

const STAGES = [
  { key: 'new_inquiry', label: 'New Inquiry', dot: 'bg-blue-500', ring: 'border-blue-500/30' },
  { key: 'contacted', label: 'Contacted', dot: 'bg-violet-500', ring: 'border-violet-500/30' },
  { key: 'proposal_sent', label: 'Proposal Sent', dot: 'bg-amber-500', ring: 'border-amber-500/30' },
  { key: 'booked', label: 'Booked', dot: 'bg-emerald-500', ring: 'border-emerald-500/30' },
  { key: 'lost', label: 'Lost', dot: 'bg-red-500', ring: 'border-red-500/30' },
  { key: 'not_available', label: 'Not Available', dot: 'bg-slate-400', ring: 'border-slate-400/30' },
];

const EVENT_TYPES = ['Wedding', 'Wedding & Reception', 'Pre-wedding', 'Mehendi', 'Haldi', 'Sangeet', 'Reception', 'Engagement', 'Photoshoot', 'Vidhi', 'Baraat', 'Other'];
const COVERAGE_TYPES = ['Photo', 'Video', 'Photo + Video', 'Engagement'];

const emptyForm = {
  name: '', email: '', phone: '', instagram: '', event_type: '', event_date: '',
  venue: '', coverage_type: '', referred_by: '', message: '', budget: '', est_value: '',
  follow_up_date: '',
};

const inputCls = 'w-full px-4 py-3 rounded-2xl bg-secondary border border-transparent focus:border-ring/40 focus:ring-2 focus:ring-ring/20 text-sm outline-none text-foreground placeholder:text-muted-foreground';

const todayStr = () => new Date().toISOString().slice(0, 10);

const nextFollowUp = (lead) => {
  const sent = new Set(lead.followups_sent || []);
  const cands = [...(lead.auto_followups || [])];
  if (lead.follow_up_date) cands.push(lead.follow_up_date);
  const pending = cands.filter((d) => d && !sent.has(d)).sort();
  return pending[0] || null;
};

const followInfo = (d) => {
  if (!d) return null;
  const due = new Date(d + 'T00:00:00');
  const today = new Date(todayStr() + 'T00:00:00');
  const diff = Math.round((due - today) / 86400000);
  const label =
    diff === 0 ? 'Follow up today'
    : diff === 1 ? 'Follow up tomorrow'
    : diff < 0 ? `Follow-up ${Math.abs(diff)}d late`
    : `Follow up ${due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  const tone =
    diff < 0 ? 'bg-red-500/10 text-red-500'
    : diff === 0 ? 'bg-amber-500/10 text-amber-500'
    : 'bg-secondary text-muted-foreground';
  return { label, tone };
};

const Leads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([leadAPI.list(), leadAPI.stats()]);
      setLeads(l.data || []);
      setStats(s.data || null);
    } catch {
      toast.error('Could not load leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refreshStats = async () => {
    try { const s = await leadAPI.stats(); setStats(s.data); } catch { /* silent */ }
  };

  const openNew = () => { setForm(emptyForm); setShowForm(true); };

  const saveLead = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, est_value: form.est_value ? parseFloat(form.est_value) : null };
      const { data } = await leadAPI.create(payload);
      setLeads((prev) => [...prev, data]);
      setShowForm(false);
      refreshStats();
      toast.success('Lead added to pipeline');
    } catch {
      toast.error('Could not save lead');
    } finally {
      setSaving(false);
    }
  };

  const moveStage = async (lead, stage) => {
    setLeads((prev) => prev.map((l) => (l.lead_id === lead.lead_id ? { ...l, stage } : l)));
    if (detail?.lead_id === lead.lead_id) setDetail((d) => ({ ...d, stage }));
    try {
      await leadAPI.update(lead.lead_id, { stage });
      refreshStats();
    } catch { load(); }
  };

  const setFollowUp = async (lead, date) => {
    setLeads((prev) => prev.map((l) => (l.lead_id === lead.lead_id ? { ...l, follow_up_date: date || null } : l)));
    if (detail?.lead_id === lead.lead_id) setDetail((d) => ({ ...d, follow_up_date: date || null }));
    try {
      await leadAPI.update(lead.lead_id, { follow_up_date: date || '' });
      if (date) toast.success('Follow-up reminder set 🔔');
    } catch { load(); }
  };

  const removeLead = async (lead) => {
    if (!window.confirm(`Delete lead "${lead.name}"? This can't be undone.`)) return;
    setLeads((prev) => prev.filter((l) => l.lead_id !== lead.lead_id));
    setDetail(null);
    try { await leadAPI.delete(lead.lead_id); refreshStats(); } catch { load(); }
  };

  const convertLead = async (lead) => {
    if (!window.confirm(`Convert "${lead.name}" into a booked Project? You can add events after.`)) return;
    try {
      const { data } = await leadAPI.convert(lead.lead_id);
      toast.success(data.already_converted ? 'Opening existing project…' : 'Project created 🎉');
      setDetail(null);
      navigate('/projects');
    } catch {
      toast.error('Could not convert to project');
    }
  };

  const byStage = (key) => leads.filter((l) => (l.stage || 'new_inquiry') === key);

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="leads-page">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-1">Leads</h1>
            <p className="text-muted-foreground text-base">Track every inquiry from first hello to booked</p>
          </div>
          <button
            onClick={openNew}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-3 rounded-full font-medium text-sm transition-all active:scale-95 shrink-0"
            data-testid="new-lead-button"
          >
            <Plus size={18} /> <span className="hidden sm:inline">New Lead</span>
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
            <StatTile icon={Inbox} label="New this month" value={stats.new_this_month} />
            <StatTile icon={Sparkles} label="Active leads" value={stats.active} />
            <StatTile icon={Briefcase} label="Booked this month" value={stats.booked_this_month} />
            <StatTile icon={TrendingUp} label="Conversion rate" value={`${stats.conversion_rate}%`} />
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {STAGES.map((s) => <div key={s.key} className="h-40 rounded-2xl bg-secondary animate-pulse" />)}
          </div>
        ) : (
          <div className="flex lg:grid lg:grid-cols-5 gap-4 overflow-x-auto pb-4 -mx-1 px-1 snap-x">
            {STAGES.map((stage) => {
              const items = byStage(stage.key);
              return (
                <div
                  key={stage.key}
                  className="min-w-[80vw] sm:min-w-[300px] lg:min-w-0 shrink-0 lg:shrink snap-start"
                  data-testid={`lead-column-${stage.key}`}
                >
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <span className={`w-2.5 h-2.5 rounded-full ${stage.dot}`} />
                    <h2 className="text-sm font-semibold tracking-tight">{stage.label}</h2>
                    <span className="ml-auto text-xs font-medium text-muted-foreground" data-testid={`lead-count-${stage.key}`}>
                      {items.length}
                    </span>
                  </div>
                  <div className="space-y-3 min-h-[60px]">
                    <AnimatePresence initial={false}>
                      {items.map((lead) => (
                        <motion.button
                          key={lead.lead_id}
                          layout
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          onClick={() => setDetail(lead)}
                          className={`w-full text-left card p-4 border ${stage.ring} hover:border-gold/50 transition-colors`}
                          data-testid={`lead-card-${lead.lead_id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-semibold tracking-tight text-sm leading-snug">{lead.name}</p>
                            {lead.est_value != null && (
                              <span className="text-xs font-medium text-emerald-500 shrink-0">${lead.est_value}</span>
                            )}
                          </div>
                          {lead.event_type && (
                            <span className="inline-block mt-2 px-2 py-0.5 rounded-full bg-gold/10 text-gold text-[11px] font-medium">
                              {lead.event_type}
                            </span>
                          )}
                          <div className="mt-2 space-y-1">
                            {lead.event_date && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                                <CalIcon size={12} /> {formatDate(lead.event_date)}
                              </p>
                            )}
                            {lead.venue && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1.5 truncate">
                                <MapPin size={12} /> {lead.venue}
                              </p>
                            )}
                          </div>
                          {(() => {
                            const f = followInfo(nextFollowUp(lead));
                            return f ? (
                              <span className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-[11px] font-medium ${f.tone}`} data-testid={`lead-followup-badge-${lead.lead_id}`}>
                                <Bell size={11} /> {f.label}
                              </span>
                            ) : null;
                          })()}
                          {lead.converted_project_id && (
                            <p className="mt-2 text-[11px] text-emerald-500 font-medium">✓ Project created</p>
                          )}
                        </motion.button>
                      ))}
                    </AnimatePresence>
                    {items.length === 0 && (
                      <div className="rounded-2xl border border-dashed border-border py-6 text-center">
                        <p className="text-xs text-muted-foreground">Nothing here</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* New Lead modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center sm:p-4" onClick={() => setShowForm(false)}>
          <div
            className="bg-card w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="lead-form-modal"
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-2xl font-semibold tracking-tight">New Lead</h2>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-full hover:bg-secondary transition-colors" aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={saveLead} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Priya & Raj" data-testid="lead-name-input" /></Field>
                <Field label="Email"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="name@email.com" data-testid="lead-email-input" /></Field>
                <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="+1 555 123 4567" data-testid="lead-phone-input" /></Field>
                <Field label="Instagram"><input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className={inputCls} placeholder="@handle" /></Field>
                <Field label="Event Type">
                  <select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} className={inputCls} data-testid="lead-eventtype-select">
                    <option value="">Select…</option>
                    {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Event Date"><input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className={inputCls} /></Field>
                <Field label="Venue / Location"><input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className={inputCls} placeholder="City or venue" /></Field>
                <Field label="Coverage Type">
                  <select value={form.coverage_type} onChange={(e) => setForm({ ...form, coverage_type: e.target.value })} className={inputCls}>
                    <option value="">Select…</option>
                    {COVERAGE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Budget"><input value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className={inputCls} placeholder="e.g. $5–7k" /></Field>
                <Field label="Est. Value ($)"><input type="number" min="0" step="any" value={form.est_value} onChange={(e) => setForm({ ...form, est_value: e.target.value })} className={inputCls} placeholder="6000" /></Field>
                <Field label="Referred By"><input value={form.referred_by} onChange={(e) => setForm({ ...form, referred_by: e.target.value })} className={inputCls} placeholder="Planner, friend…" /></Field>
                <Field label="Follow up on"><input type="date" min={todayStr()} value={form.follow_up_date} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} className={inputCls} data-testid="lead-followup-input" /></Field>
              </div>
              <Field label="Message"><textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows="3" className={inputCls} placeholder="What they wrote in the inquiry…" /></Field>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className="flex-1 bg-primary text-primary-foreground px-6 py-3.5 rounded-full font-medium text-sm transition-all active:scale-[0.97] disabled:opacity-50" data-testid="lead-save-button">
                  {saving ? 'Saving…' : 'Add Lead'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="px-6 py-3.5 rounded-full bg-secondary text-sm font-medium">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lead detail modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center sm:p-4" onClick={() => setDetail(null)}>
          <div
            className="bg-card w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="lead-detail-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-2xl font-semibold tracking-tight">{detail.name}</h2>
                {detail.event_type && <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-gold/10 text-gold text-xs font-medium">{detail.event_type}</span>}
              </div>
              <button onClick={() => setDetail(null)} className="p-2 rounded-full hover:bg-secondary transition-colors" aria-label="Close">
                <X size={20} />
              </button>
            </div>

            {/* Stage picker */}
            <div className="mb-5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Stage</p>
              <div className="flex flex-wrap gap-2">
                {STAGES.map((s) => {
                  const active = (detail.stage || 'new_inquiry') === s.key;
                  return (
                    <button
                      key={s.key}
                      onClick={() => moveStage(detail, s.key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all active:scale-95 ${active ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}
                      data-testid={`stage-set-${s.key}`}
                    >
                      <span className={`w-2 h-2 rounded-full ${s.dot}`} /> {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Follow-up reminder */}
            <div className="mb-5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Follow-up reminder</p>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Bell size={15} className="text-gold" />
                </span>
                <input
                  type="date"
                  min={todayStr()}
                  value={detail.follow_up_date || ''}
                  onChange={(e) => setFollowUp(detail, e.target.value)}
                  className="flex-1 px-4 py-2.5 rounded-2xl bg-secondary border border-transparent focus:border-ring/40 text-sm outline-none text-foreground"
                  data-testid="lead-followup-edit"
                />
                {detail.follow_up_date && (
                  <button
                    onClick={() => setFollowUp(detail, '')}
                    className="p-2 rounded-full text-muted-foreground hover:text-destructive transition-colors active:scale-90"
                    data-testid="lead-followup-clear"
                    aria-label="Clear follow-up"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">Auto-reminders at 2, 4 &amp; 6 days show in your To-Do list. Set your own date here anytime.</p>
            </div>

            <div className="space-y-2.5 mb-5">
              {detail.email && <DetailRow icon={Mail} value={detail.email} href={`mailto:${detail.email}`} />}
              {detail.phone && <DetailRow icon={Phone} value={detail.phone} href={`tel:${detail.phone}`} />}
              {detail.instagram && <DetailRow icon={Instagram} value={`@${detail.instagram}`} href={`https://instagram.com/${detail.instagram}`} />}
              {detail.event_date && <DetailRow icon={CalIcon} value={formatDate(detail.event_date)} />}
              {detail.venue && <DetailRow icon={MapPin} value={detail.venue} />}
              {detail.coverage_type && <DetailRow icon={Briefcase} value={`Coverage: ${detail.coverage_type}`} />}
              {detail.budget && <DetailRow icon={DollarSign} value={`Budget: ${detail.budget}`} />}
              {detail.est_value != null && <DetailRow icon={TrendingUp} value={`Est. value: $${detail.est_value}`} />}
              {detail.referred_by && <p className="text-sm text-muted-foreground pl-6">Referred by {detail.referred_by}</p>}
            </div>

            {detail.message && (
              <div className="mb-5 p-4 rounded-2xl bg-secondary">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Message</p>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{detail.message}</p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              {detail.converted_project_id ? (
                <button onClick={() => navigate('/projects')} className="flex-1 flex items-center justify-center gap-2 bg-secondary text-foreground px-5 py-3.5 rounded-full font-medium text-sm transition-all active:scale-[0.97]" data-testid="open-project-button">
                  <Briefcase size={16} /> Open Project
                </button>
              ) : (
                <button onClick={() => convertLead(detail)} className="flex-1 flex items-center justify-center gap-2 bg-primary text-primary-foreground px-5 py-3.5 rounded-full font-medium text-sm transition-all active:scale-[0.97]" data-testid="convert-lead-button">
                  <ArrowRight size={16} /> Convert to Project
                </button>
              )}
              <button onClick={() => removeLead(detail)} className="flex items-center justify-center gap-2 px-5 py-3.5 rounded-full text-destructive hover:bg-destructive/10 font-medium text-sm transition-colors" data-testid="delete-lead-button">
                <Trash2 size={16} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

const StatTile = ({ icon: Icon, label, value }) => (
  <div className="card p-4 sm:p-5" data-testid={`lead-stat-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
    <Icon className="text-gold mb-2 sm:mb-3" size={20} />
    <p className="text-2xl sm:text-3xl font-semibold tracking-tight mb-0.5">{value}</p>
    <p className="text-xs sm:text-sm font-medium text-muted-foreground">{label}</p>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-muted-foreground mb-1.5">{label}</label>
    {children}
  </div>
);

const DetailRow = ({ icon: Icon, value, href }) => (
  <div className="flex items-center gap-2.5 text-sm">
    <Icon size={15} className="text-muted-foreground shrink-0" />
    {href ? (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-gold transition-colors truncate">{value}</a>
    ) : (
      <span className="text-foreground truncate">{value}</span>
    )}
  </div>
);

export default Leads;
